import { useTelefoneInterno, type ServidorDeRede, type Telefone as Aparelho } from '@mymaxx/ui';
import { Mic, MicOff, Phone, PhoneOff } from 'lucide-react';
import { useEffect } from 'react';
import { api } from '../lib/api';
import { useDiscar } from '../lib/discar';
import { useSocketInterno } from '../lib/interno-tempo-real';
import { pararToque, tocarToque } from '../lib/som';

/**
 * A chamada de voz no app do técnico.
 *
 * Mesmo motor e mesma sinalização do painel (`@mymaxx/ui`): o que muda aqui é só o
 * tamanho da janela — no celular ela toma a tela inteira, porque atender uma ligação com
 * uma luva na mão e o celular no colo não é o mesmo gesto de clicar num cantinho.
 *
 * Fica no alto do aplicativo: o técnico pode estar no histórico, na conta ou num
 * atendimento quando o telefone toca.
 */

export function Telefone() {
  const socket = useSocketInterno();
  const chamada = useTelefoneInterno(socket, buscarServidores);

  const definir = useDiscar((e) => e.definir);
  useEffect(() => {
    definir(chamada.podeLigar ? chamada.ligarPara : null);
    return () => definir(null);
  }, [chamada.podeLigar, definir]);

  // Toca E vibra. A vibração está aqui desde sempre porque o técnico fica no barulho da
  // rua ou de fone; o som entrou em 21/10, quando o arquivo de toque passou a existir
  // também neste app. Os dois juntos porque cada um alcança uma situação: o celular no
  // bolso avisa pela vibração, o apoiado na bancada avisa pelo som.
  //
  // O navegador pode recusar o áudio antes do primeiro toque na tela (autoplay). Por isso
  // a vibração não depende dele: se o som não sair, ela sai.
  useEffect(() => {
    if (chamada.estado !== 'tocando') return;
    tocarToque();
    const id = setInterval(() => {
      try {
        navigator.vibrate?.([400, 600]);
      } catch {
        /* aparelho sem vibração: a tela cheia já avisa */
      }
    }, 1000);
    return () => {
      clearInterval(id);
      pararToque();
      try {
        navigator.vibrate?.(0);
      } catch {
        /* nada a fazer */
      }
    };
  }, [chamada.estado]);

  return <TelaDaChamada chamada={chamada} />;
}

async function buscarServidores(): Promise<ServidorDeRede[]> {
  const { servidores } = await api.get<{ servidores: ServidorDeRede[]; toqueMaximoMs: number }>(
    '/v1/interno/chamadas/credenciais',
  );
  return servidores;
}

/** A chamada ocupando a tela: no celular, uma ligação não divide espaço com nada. */
function TelaDaChamada({ chamada }: { chamada: Aparelho }) {
  if (chamada.estado === 'parada') return null;

  const tocando = chamada.estado === 'tocando';
  const falando = chamada.estado === 'falando';

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-neutral-900/95 p-8 text-white">
      <span
        className={`flex size-24 items-center justify-center rounded-full bg-white/10 ${
          tocando ? 'animate-pulse' : ''
        }`}
      >
        <Phone size={40} />
      </span>
      <div className="text-center">
        <p className="text-lg font-medium">{chamada.comQuem?.nome ?? 'Chamada'}</p>
        <p className="mt-1 text-sm text-white/70">
          {tocando
            ? 'está ligando para você'
            : chamada.estado === 'chamando'
              ? 'chamando…'
              : chamada.estado === 'conectando'
                ? 'conectando…'
                : relogio(chamada.duracao)}
        </p>
        {chamada.erro && <p className="mt-2 text-sm text-red-300">{chamada.erro}</p>}
      </div>

      <div className="flex items-center gap-6">
        {falando && (
          <button
            type="button"
            aria-label={chamada.mudo ? 'Ligar o microfone' : 'Desligar o microfone'}
            onClick={chamada.alternarMudo}
            className={`flex size-14 items-center justify-center rounded-full ${
              chamada.mudo ? 'bg-amber-400 text-amber-950' : 'bg-white/15'
            }`}
          >
            {chamada.mudo ? <MicOff size={22} /> : <Mic size={22} />}
          </button>
        )}
        {tocando && (
          <button
            type="button"
            aria-label="Atender"
            onClick={() => void chamada.atender()}
            className="flex size-16 items-center justify-center rounded-full bg-green-600"
          >
            <Phone size={26} />
          </button>
        )}
        <button
          type="button"
          aria-label={tocando ? 'Recusar' : 'Desligar'}
          onClick={() => (tocando ? chamada.recusar() : chamada.desligar())}
          className="flex size-16 items-center justify-center rounded-full bg-red-600"
        >
          <PhoneOff size={26} />
        </button>
      </div>
    </div>
  );
}

/** "0:42", "12:03" — o relógio da conversa. */
function relogio(segundos: number): string {
  const m = Math.floor(segundos / 60);
  const s = segundos % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
